import { useCallback, useEffect, useRef, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { CommandLineIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import StationEventService from '../../../services/StationEventService';
import { StationEvent, StationEventType } from '../../../services/models/stationEvent';
import { Lock } from '../../../services/models/assets';
import EventRow, { EVENT_TYPE_LABELS } from './EventRow';

const PAGE_SIZE = 50;

interface ConsolePanelProps {
  stationId: number;
  locks: Lock[];
}

const ConsolePanel: React.FC<ConsolePanelProps> = ({ stationId, locks }) => {
  const [typeFilter, setTypeFilter] = useState<StationEventType | ''>('');
  const [lockFilter, setLockFilter] = useState<number | ''>('');
  const scrollRef = useRef<HTMLDivElement>(null);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['station-events', stationId, typeFilter, lockFilter],
    queryFn: ({ pageParam = 0 }) =>
      StationEventService.getStationEvents(stationId, {
        type: typeFilter || undefined,
        lockId: lockFilter || undefined,
        limit: PAGE_SIZE,
        offset: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, p) => sum + p.events.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    refetchInterval: 5000,
    refetchIntervalInBackground: false,
  });

  // All events across pages (newest first from API)
  const events = data?.pages.flatMap((p) => p.events) ?? [];
  const latestEventId = events[0]?.id;

  // Auto-scroll to bottom when new events arrive (only if user is near the bottom)
  const isNearBottomRef = useRef(true);
  useEffect(() => {
    if (latestEventId && isNearBottomRef.current) {
      const el = scrollRef.current;
      if (el) el.scrollTop = 0;
    }
  }, [latestEventId]);

  // Load older events when scrolling to top
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    // In flex-col-reverse, scrollTop is 0 at bottom (newest), negative when scrolled up.
    isNearBottomRef.current = el.scrollTop >= -100;
    if (!isFetchingNextPage && hasNextPage) {
      // Trigger load when close to the visual top.
      if (el.scrollTop <= -(el.scrollHeight - el.clientHeight - 40)) {
        fetchNextPage();
      }
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  // Attach scroll listener
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  return (
    <div className="bg-white rounded-xl shadow-card outline outline-1 outline-offset-[-1px] outline-neutral-10 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-neutral-10">
        <div className="flex items-center gap-2">
          <CommandLineIcon className="w-5 h-5 text-primary-500" />
          <h3 className="typo-h3 text-neutral">Console</h3>
          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-orange-100 text-primary-500 typo-small !font-bold">
            Live
          </span>
        </div>
        {/* Filters */}
        <div className="flex items-center gap-2">
          <div className="relative">
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as StationEventType | '')}
              className={`appearance-none pl-3 pr-8 py-1.5 rounded-md border border-neutral-10 transition-colors typo-body cursor-pointer ${
                typeFilter ? 'bg-neutral text-white' : 'bg-white text-neutral-60 hover:text-neutral hover:bg-neutral-5'
              }`}
            >
              <option value="">Tous les types</option>
              {Object.entries(EVENT_TYPE_LABELS).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
            <ChevronDownIcon className={`pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${
              typeFilter ? 'text-white' : 'text-neutral-40'
            }`} strokeWidth={3} />
          </div>
          <div className="relative">
            <select
              value={lockFilter}
              onChange={(e) => setLockFilter(e.target.value ? Number(e.target.value) : '')}
              className={`appearance-none pl-3 pr-8 py-1.5 rounded-md border border-neutral-10 transition-colors typo-body cursor-pointer ${
                lockFilter ? 'bg-neutral text-white' : 'bg-white text-neutral-60 hover:text-neutral hover:bg-neutral-5'
              }`}
            >
              <option value="">Tous les locks</option>
              {locks.map((lock) => (
                <option key={lock.id} value={lock.id}>Lock {lock.position + 1}</option>
              ))}
            </select>
            <ChevronDownIcon className={`pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${
              lockFilter ? 'text-white' : 'text-neutral-40'
            }`} strokeWidth={3} />
          </div>
        </div>
      </div>

      {/* Horizontally scrollable table region so all columns stay reachable on narrow screens */}
      <div className="overflow-x-auto">
        <div className="min-w-[640px]">
          {/* Column headers */}
          <div className="flex items-center px-5 py-2 border-b border-neutral-10">
            <span className="w-[130px] flex-shrink-0 typo-small tracking-[0.03em] !font-bold uppercase text-neutral-40">Horodatage</span>
            <span className="w-40 flex-shrink-0 typo-small tracking-[0.03em] !font-bold uppercase text-neutral-40">Type</span>
            <span className="w-[70px] flex-shrink-0 typo-small tracking-[0.03em] !font-bold uppercase text-neutral-40">Lock</span>
            <span className="flex-grow typo-small tracking-[0.03em] !font-bold uppercase text-neutral-40">Détails</span>
            <span className="w-[70px] flex-shrink-0 typo-small tracking-[0.03em] !font-bold uppercase text-neutral-40">Statut</span>
          </div>

          {/* Rows — reversed so newest is at the bottom, scroll up for older */}
          <div
            ref={scrollRef}
            className="max-h-[450px] overflow-y-auto flex flex-col-reverse"
          >
            {events.length === 0 ? (
              <div className="py-8 text-center typo-small text-neutral-40">
                Aucun événement
              </div>
            ) : (
              <>
                {events.map((event: StationEvent) => (
                  <EventRow key={event.id} event={event} />
                ))}
                {isFetchingNextPage && (
                  <div className="py-2 text-center typo-caption text-neutral-40">
                    Chargement…
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};


export default ConsolePanel;
